/** Portable, JSON-only replay event. Payloads are validated before replay. */
export interface SupportEvent { type: number; timestamp: number; data: any }
export type SupportErrorCode = 'Unavailable' | 'Disabled' | 'AlreadyRecording' | 'NotFound' | 'InvalidOptions' | 'InvalidReport' | 'StorageFailed' | 'RecordingFailed' | 'LimitReached' | 'Destroyed'
export class SupportError extends Error {
  constructor(public readonly code: SupportErrorCode, message: string) {
    super(message)
    this.name = `Support${code}Error`
  }
}
export interface SupportState {
  phase: 'idle' | 'starting' | 'recording' | 'stopping'
  recordingId: string | null
  startedAt: number | null
  elapsedMs: number
  mode: 'manual' | 'flight' | null
  error: { code: SupportErrorCode; message: string } | null
}
export interface SupportStartOptions {
  /** Explicit assertion from the caller's consent flow; never inferred from a feature flag. */
  consent: true
  mode?: 'manual' | 'flight'
  historySeconds?: number
  maxDurationSeconds?: number
  maxBytes?: number
  /** Memory is the default. IndexedDB requires a scope identifying application + user. */
  storage?: 'memory' | 'indexeddb'
  storageScope?: string
  /** Exact sensitive boundary selectors the user explicitly agreed to share, for this recording only. */
  shareSelectors?: string[]
}
export interface SupportRecordingInfo {
  status: 'recording' | 'complete' | 'incomplete'
  id: string
  startedAt: number
  endedAt: number
  durationMs: number
  bytes: number
  incomplete: boolean
  reason?: string
  /** Safe export start points, relative to startedAt. */
  checkpoints: number[]
}
export interface SupportAnnotation {
  id: string
  kind: 'pointer' | 'rectangle' | 'text'
  startMs: number
  endMs: number
  text: string
  /** Coordinates relative to the recorded viewport, in [0, 1]. */
  x: number
  y: number
  width?: number
  height?: number
}
export interface SupportReport {
  format: 'aeppic.support'
  version: 1
  recorder: { name: 'rrweb'; version: '2.1.4' }
  id: string
  title: string
  description: string
  startedAt: number
  durationMs: number
  incomplete: boolean
  privacy: { sharedSensitiveRegions: boolean; assets: 'excluded' | 'embedded' }
  events: SupportEvent[]
  annotations: SupportAnnotation[]
}
export interface SupportReportOptions {
  title?: string
  description?: string
  /** Start must be a recording checkpoint; arbitrary start trims are rejected, never merely hidden. */
  trim?: { startMs: number; endMs: number }
  annotations?: SupportAnnotation[]
}
export interface SupportPlayback {
  /** Magnification and normalized focus point; omitting options resets to fit. */
  setView(options?: { zoom: number; centerX: number; centerY: number }): void
  play(): void
  pause(): void
  seek(timeMs: number): void
  readonly timeMs: number
  readonly durationMs: number
  readonly annotations: readonly SupportAnnotation[]
  subscribe(listener: (timeMs: number) => void): () => void
  destroy(): void
}
export interface SupportApi {
  readonly available: boolean
  readonly state: Readonly<SupportState>
  subscribe(listener: (state: Readonly<SupportState>) => void): () => void
  start(options: SupportStartOptions): Promise<SupportRecordingInfo>
  enableFlightRecorder(options: SupportStartOptions): Promise<SupportRecordingInfo>
  stop(): Promise<SupportRecordingInfo | null>
  /** Select a persisted store before listing drafts after reload. No recording starts. */
  openStore(scope: string): Promise<void>
  list(): Promise<SupportRecordingInfo[]>
  get(id: string): Promise<SupportRecordingInfo>
  delete(id: string): Promise<void>
  clear(): Promise<void>
  buildReport(id: string, options?: SupportReportOptions): Promise<SupportReport>
  serializeReport(report: SupportReport): string
  parseReport(text: string): SupportReport
  createPlayback(options: { report: SupportReport; target: HTMLElement }): Promise<SupportPlayback>
  destroy(): void
}
export interface SupportHost { enabled(): boolean }
